import { useState, useEffect, useCallback } from 'react'
import {
  Lightbulb, Rocket, Brain, Cpu, RefreshCw, Search,
  TrendingUp, Star, GitBranch, Zap,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const CATEGORY_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  ai_ml:         { icon: Brain,     color: 'text-purple-light',  label: 'AI / ML' },
  quantum:       { icon: Cpu,       color: 'text-cyan-light',    label: 'Quantum' },
  blockchain:    { icon: GitBranch, color: 'text-blue-light',    label: 'Blockchain' },
  biotech:       { icon: Zap,       color: 'text-emerald-light', label: 'Biotech' },
  space:         { icon: Rocket,    color: 'text-amber-light',   label: 'Space' },
  robotics:      { icon: Cpu,       color: 'text-rose-light',    label: 'Robotics' },
  green_tech:    { icon: Lightbulb, color: 'text-emerald-light', label: 'Green Tech' },
  semiconductor: { icon: Cpu,       color: 'text-blue-light',    label: 'Semiconductor' },
}

const IMPACT_STYLES: Record<string, { bg: string; text: string }> = {
  high:   { bg: 'bg-rose/10',    text: 'text-rose-light' },
  medium: { bg: 'bg-amber/10',   text: 'text-amber-light' },
  low:    { bg: 'bg-emerald/10', text: 'text-emerald-light' },
}

const LANG_COLORS: Record<string, string> = {
  TypeScript: 'bg-blue', Python: 'bg-amber', Rust: 'bg-rose', Go: 'bg-cyan', JavaScript: 'bg-amber-light',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function InnovationPage() {
  const [innovations, setInnovations] = useState<any[]>([])
  const [trending, setTrending] = useState<any[]>([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [feedRes, ghRes] = await Promise.allSettled([
        api.get('/api/innovation/feed', { params: { limit: 30, category: categoryFilter !== 'all' ? categoryFilter : undefined } }),
        api.get('/api/innovation/github-trending'),
      ])
      if (feedRes.status === 'fulfilled') setInnovations(feedRes.value.data.signals || [])
      if (ghRes.status === 'fulfilled') setTrending(ghRes.value.data.repos || [])
    } finally {
      setLoading(false)
    }
  }, [categoryFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 300_000); return () => clearInterval(i) }, [fetchData])

  const filtered = innovations.filter(item => {
    if (searchTerm && !item.title.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Lightbulb size={24} className="text-purple" /> Innovation & R&D
          </h1>
          <p className="text-sm text-text-secondary mt-1">Live from ArXiv, MIT Tech Review, IEEE Spectrum, VentureBeat, GitHub</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search innovations..." className="input pl-9 py-2 text-xs" />
        </div>
        <select title="Filter by category" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="input w-auto py-2 text-xs">
          <option value="all">All Categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
      </div>

      {loading && !innovations.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching from ArXiv, GitHub, and RSS feeds...</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Rocket size={16} className="text-purple" /> Innovation Feed
          </h2>
          {filtered.map((item, idx) => {
            const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.ai_ml
            const imp = IMPACT_STYLES[item.impact] || IMPACT_STYLES.medium
            const CatIcon = cat.icon
            return (
              <div key={idx} className="card-hover p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CatIcon size={14} className={cat.color} />
                  <span className={clsx('badge text-[10px]', `${cat.color} bg-surface-hover`)}>{cat.label}</span>
                  <span className={clsx('badge text-[10px]', imp.bg, imp.text)}>{item.impact} impact</span>
                  <span className="text-[10px] text-text-muted ml-auto">{item.source}</span>
                </div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">
                  {item.url ? <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a> : item.title}
                </h3>
                <p className="text-xs text-text-secondary mt-1 leading-relaxed">{item.summary}</p>
                <div className="flex items-center gap-2 mt-2">
                  {(item.tags || []).map((tag: string) => (
                    <span key={tag} className="text-[10px] text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">{tag}</span>
                  ))}
                  {(item.authors || []).length > 0 && (
                    <span className="text-[10px] text-text-muted ml-auto">{item.authors.join(', ')}</span>
                  )}
                  <span className="text-[10px] text-text-muted ml-auto">{relativeTime(item.timestamp)}</span>
                </div>
              </div>
            )
          })}
          {!loading && filtered.length === 0 && <p className="text-sm text-text-muted text-center py-8">No innovations match filters</p>}
        </div>

        <div>
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <GitBranch size={16} className="text-text-muted" /> GitHub Trending
          </h2>
          <div className="space-y-2">
            {trending.map((repo, idx) => (
              <div key={repo.full_name || idx} className="card-hover p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={clsx('w-2.5 h-2.5 rounded-full', LANG_COLORS[repo.language] || 'bg-text-muted')} />
                  <span className="text-xs text-text-muted">{repo.language}</span>
                </div>
                <h4 className="text-sm font-semibold text-blue-light">
                  <a href={repo.url} target="_blank" rel="noopener" className="hover:underline">{repo.full_name}</a>
                </h4>
                <p className="text-[11px] text-text-secondary mt-0.5 leading-relaxed line-clamp-2">{repo.description}</p>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-text-muted">
                  <span className="flex items-center gap-1"><Star size={10} /> {(repo.stars / 1000).toFixed(1)}k</span>
                  <span className="flex items-center gap-1"><GitBranch size={10} /> {(repo.forks / 1000).toFixed(1)}k</span>
                  {(repo.topics || []).slice(0, 2).map((t: string) => (
                    <span key={t} className="bg-surface-hover px-1 py-0.5 rounded">{t}</span>
                  ))}
                </div>
              </div>
            ))}
            {trending.length === 0 && !loading && <p className="text-xs text-text-muted">No trending repos loaded</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
